import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Feed com rolagem infinita que deixa o rodapé ser alcançado.
 *
 * O catálogo tem milhares de ofertas (6.718 em 2026-09-13) e a API responde
 * `has_more` até o fim. Com rolagem infinita pura o rodapé da Home nunca chegava
 * a ficar na tela: cada vez que aparecia, um lote novo o empurrava para baixo.
 *
 * Aqui a rolagem continua automática nos primeiros `lotesAutomaticos` lotes. Depois
 * disso ela para e o feed mostra um botão "Carregar mais ofertas" logo antes do
 * rodapé; cada clique traz um lote e o catálogo segue do mesmo ponto.
 *
 * Garantias (cobertas em feed-infinito.test.js):
 *  - um pedido de próxima página por vez (trava síncrona, não depende de render);
 *  - nenhum `skip` é pedido duas vezes na mesma consulta;
 *  - o observador se desliga antes de pedir e só volta depois da resposta, então
 *    não existe laço: no máximo um pedido por lote automático;
 *  - atingido o limite, nada mais é buscado sozinho, nem em segundo plano;
 *  - trocar filtro ou ordenação descarta respostas antigas e recomeça do zero;
 *  - fim real do catálogo (`has_more` falso ou lote vazio) encerra o feed.
 *
 * `montarUrl(skip)` monta a URL de uma página; `chave` identifica a consulta
 * (filtro + ordenação): mudou a chave, o feed recomeça.
 */
export default function useCatalogoInfinito({ montarUrl, chave, lotesAutomaticos = 4, margem = '0px 0px 1200px 0px', aoCarregarPrimeira }) {
    const [itens, setItens] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [carregandoMais, setCarregandoMais] = useState(false);
    const [temMais, setTemMais] = useState(true);
    const [lotesAuto, setLotesAuto] = useState(0);
    const [sentinela, setSentinela] = useState(null);

    const emVoo = useRef(false);
    const pedidos = useRef(new Set());
    const geracao = useRef(0);
    const proximoSkip = useRef(0);
    const lotesAutoRef = useRef(0);
    const temMaisRef = useRef(true);
    const chaveCarregada = useRef(null);
    const montarUrlRef = useRef(montarUrl);
    const aoCarregarPrimeiraRef = useRef(aoCarregarPrimeira);
    montarUrlRef.current = montarUrl;
    aoCarregarPrimeiraRef.current = aoCarregarPrimeira;

    const buscar = useCallback(async (origem) => {
        const inicial = origem === 'inicial';
        const skip = inicial ? 0 : proximoSkip.current;
        if (!inicial) {
            if (emVoo.current || !temMaisRef.current) return false;
            if (origem === 'auto' && lotesAutoRef.current >= lotesAutomaticos) return false;
        }
        if (pedidos.current.has(skip)) return false;
        pedidos.current.add(skip);
        emVoo.current = true;
        const minha = geracao.current;
        if (origem === 'auto') { lotesAutoRef.current += 1; setLotesAuto(lotesAutoRef.current); }
        if (inicial) setCarregando(true); else setCarregandoMais(true);

        try {
            const resposta = await fetch(montarUrlRef.current(skip));
            const dados = await resposta.json();
            if (minha !== geracao.current) return false;
            const novos = Array.isArray(dados.deals) ? dados.deals : [];
            if (inicial) {
                setItens(novos);
                if (aoCarregarPrimeiraRef.current) aoCarregarPrimeiraRef.current(novos);
            } else {
                setItens((atual) => {
                    const vistos = new Set(atual.map((d) => d.deal_id));
                    return [...atual, ...novos.filter((d) => !vistos.has(d.deal_id))];
                });
            }
            proximoSkip.current = skip + novos.length;
            temMaisRef.current = !!dados.has_more && novos.length > 0;
            setTemMais(temMaisRef.current);
            return true;
        } catch (erro) {
            if (minha !== geracao.current) return false;
            console.error('Error fetching deals:', erro);
            // Falhou: o mesmo lote pode ser pedido de novo, mas só por clique. A
            // rolagem automática para, para uma API fora do ar não virar laço.
            pedidos.current.delete(skip);
            lotesAutoRef.current = lotesAutomaticos;
            setLotesAuto(lotesAutomaticos);
            if (inicial) {
                setItens([]);
                if (aoCarregarPrimeiraRef.current) aoCarregarPrimeiraRef.current(null);
            }
            return false;
        } finally {
            if (minha === geracao.current) {
                emVoo.current = false;
                if (inicial) setCarregando(false); else setCarregandoMais(false);
            }
        }
    }, [lotesAutomaticos]);

    const recomecar = useCallback(() => {
        geracao.current += 1;
        pedidos.current = new Set();
        proximoSkip.current = 0;
        lotesAutoRef.current = 0;
        temMaisRef.current = true;
        emVoo.current = false;
        setItens([]);
        setTemMais(true);
        setLotesAuto(0);
        setCarregandoMais(false);
        return buscar('inicial');
    }, [buscar]);

    // Consulta nova (ou primeira): recomeça. No StrictMode do desenvolvimento o
    // efeito roda duas vezes com a mesma chave; a segunda não repete o pedido.
    useEffect(() => {
        if (chaveCarregada.current === chave) return;
        chaveCarregada.current = chave;
        recomecar();
    }, [chave, recomecar]);

    const podeAutomatico = !carregando && !carregandoMais && temMais && lotesAuto < lotesAutomaticos;

    useEffect(() => {
        if (!podeAutomatico || !sentinela || typeof IntersectionObserver === 'undefined') return undefined;
        const observador = new IntersectionObserver((entradas) => {
            if (!entradas.some((e) => e.isIntersecting)) return;
            observador.disconnect();
            buscar('auto');
        }, { rootMargin: margem });
        observador.observe(sentinela);
        return () => observador.disconnect();
    }, [podeAutomatico, sentinela, itens.length, buscar, margem]);

    return {
        itens,
        carregando,
        carregandoMais,
        temMais,
        lotesAutomaticosFeitos: lotesAuto,
        automaticoEsgotado: lotesAuto >= lotesAutomaticos,
        sentinelaRef: setSentinela,
        carregarMais: () => buscar('manual'),
        recarregar: recomecar,
    };
}
